<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import dartsHubApi from '@/axios/dartsHubApi';

interface PartnerClient {
  id: number;
  client_id: string;
  name: string;
  redirect_uris: string[];
  active: boolean;
}

const clients = ref<PartnerClient[]>([]);
const loading = ref(true);

const parseUris = (text: string): string[] =>
  text.split('\n').map(u => u.trim()).filter(Boolean);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await dartsHubApi.get('/partners/clients');
    clients.value = data;
  } catch {
    toast.error('Erreur de chargement.');
  } finally {
    loading.value = false;
  }
};

// ── Création ──
const showCreateModal = ref(false);
const createForm = ref({ client_id: '', name: '', redirect_uris: '' });
const savingCreate = ref(false);

const openCreateModal = () => {
  createForm.value = { client_id: '', name: '', redirect_uris: '' };
  showCreateModal.value = true;
};

const saveCreate = async () => {
  const redirect_uris = parseUris(createForm.value.redirect_uris);
  if (!createForm.value.client_id || !createForm.value.name || redirect_uris.length === 0) {
    toast.error('client_id, nom et au moins une redirect_uri sont requis.');
    return;
  }
  savingCreate.value = true;
  try {
    await dartsHubApi.post('/partners/clients', {
      client_id: createForm.value.client_id.trim(),
      name: createForm.value.name.trim(),
      redirect_uris,
    });
    toast.success('Partenaire créé.');
    showCreateModal.value = false;
    await load();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    savingCreate.value = false;
  }
};

// ── Édition ──
const showEditModal = ref(false);
const editClient = ref<PartnerClient | null>(null);
const editForm = ref({ name: '', redirect_uris: '', active: true });
const savingEdit = ref(false);

const openEditModal = (client: PartnerClient) => {
  editClient.value = client;
  editForm.value = {
    name: client.name,
    redirect_uris: client.redirect_uris.join('\n'),
    active: client.active,
  };
  showEditModal.value = true;
};

const saveEdit = async () => {
  if (!editClient.value) return;
  const redirect_uris = parseUris(editForm.value.redirect_uris);
  if (!editForm.value.name || redirect_uris.length === 0) {
    toast.error('Nom et au moins une redirect_uri sont requis.');
    return;
  }
  savingEdit.value = true;
  try {
    await dartsHubApi.patch(`/partners/clients/${editClient.value.client_id}`, {
      name: editForm.value.name.trim(),
      redirect_uris,
      active: editForm.value.active,
    });
    toast.success('Partenaire mis à jour.');
    showEditModal.value = false;
    await load();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  } finally {
    savingEdit.value = false;
  }
};

const toggleActive = async (client: PartnerClient) => {
  try {
    await dartsHubApi.patch(`/partners/clients/${client.client_id}`, { active: !client.active });
    toast.success(client.active ? 'Partenaire désactivé.' : 'Partenaire réactivé.');
    await load();
  } catch (err: any) {
    toast.error(err.response?.data?.detail || 'Erreur.');
  }
};

onMounted(load);
</script>

<template>
  <div class="page">
    <div class="page-header">
      <div style="display:flex;align-items:center;gap:0.75rem;flex-wrap:wrap">
        <h1><i class="fa-solid fa-handshake" /> Partenaires</h1>
        <button class="btn btn-primary btn-sm" @click="openCreateModal">
          <i class="fa-solid fa-plus" /> Ajouter un partenaire
        </button>
        <RouterLink to="/partenaires/integration" class="btn btn-ghost btn-sm" target="_blank">
          <i class="fa-solid fa-book" /> Page d'intégration
        </RouterLink>
      </div>
    </div>

    <p class="page-hint">
      Un partenaire ne reçoit qu'un token d'identité restreint (email, nom, pseudo), jamais de rôle,
      licence ni club. La connexion n'aboutit que si l'URL de callback envoyée par le partenaire
      correspond exactement à l'une des <code>redirect_uris</code> enregistrées ici.
    </p>

    <div v-if="loading" class="loader"><i class="fa-solid fa-spinner fa-spin" /> Chargement…</div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Statut</th>
            <th>client_id</th>
            <th>Nom</th>
            <th>Redirect URIs</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="client in clients" :key="client.id">
            <td>
              <span v-if="client.active" class="badge badge-green"><i class="fa-solid fa-check" /> Actif</span>
              <span v-else class="badge badge-red"><i class="fa-solid fa-ban" /> Désactivé</span>
            </td>
            <td><code>{{ client.client_id }}</code></td>
            <td>{{ client.name }}</td>
            <td>
              <div v-for="uri in client.redirect_uris" class="uri-line" :key="uri">{{ uri }}</div>
            </td>
            <td style="display:flex;gap:0.25rem">
              <button class="btn btn-ghost btn-sm" title="Modifier" @click="openEditModal(client)">
                <i class="fa-solid fa-pen" />
              </button>
              <button class="btn btn-ghost btn-sm" :title="client.active ? 'Désactiver' : 'Réactiver'"
                @click="toggleActive(client)">
                <i class="fa-solid" :class="client.active ? 'fa-ban' : 'fa-rotate-left'" />
              </button>
            </td>
          </tr>
          <tr v-if="clients.length === 0">
            <td colspan="5" style="text-align:center;color:var(--text-muted)">Aucun partenaire enregistré</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal création -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="showCreateModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>Ajouter un partenaire</h2>
          <button class="btn btn-ghost btn-sm" @click="showCreateModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-group">
          <label>client_id</label>
          <input v-model="createForm.client_id" type="text" placeholder="ex: dartup" />
        </div>
        <div class="form-group">
          <label>Nom</label>
          <input v-model="createForm.name" type="text" placeholder="ex: DartUp" />
        </div>
        <div class="form-group">
          <label>Redirect URIs (une par ligne)</label>
          <textarea v-model="createForm.redirect_uris" rows="3" style="resize:vertical"
            placeholder="https://dartup.fr/auth/dartshub/callback" />
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="showCreateModal = false">Annuler</button>
          <button class="btn btn-primary btn-sm" :disabled="savingCreate" @click="saveCreate">
            <i v-if="savingCreate" class="fa-solid fa-spinner fa-spin" />
            Créer
          </button>
        </div>
      </div>
    </div>

    <!-- Modal édition -->
    <div v-if="showEditModal" class="modal-overlay" @click.self="showEditModal = false">
      <div class="modal">
        <div class="modal-header">
          <h2>Modifier <span class="badge badge-gray">{{ editClient?.client_id }}</span></h2>
          <button class="btn btn-ghost btn-sm" @click="showEditModal = false"><i class="fa-solid fa-xmark" /></button>
        </div>
        <div class="form-group">
          <label>Nom</label>
          <input v-model="editForm.name" type="text" />
        </div>
        <div class="form-group">
          <label>Redirect URIs (une par ligne)</label>
          <textarea v-model="editForm.redirect_uris" rows="3" style="resize:vertical" />
        </div>
        <div class="form-group">
          <label class="checkbox-label">
            <input v-model="editForm.active" type="checkbox" />
            Actif
          </label>
        </div>
        <div class="form-actions">
          <button class="btn btn-ghost btn-sm" @click="showEditModal = false">Annuler</button>
          <button class="btn btn-primary btn-sm" :disabled="savingEdit" @click="saveEdit">
            <i v-if="savingEdit" class="fa-solid fa-spinner fa-spin" />
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-hint {
  color: var(--text-muted);
  font-size: 0.85rem;
  line-height: 1.6;
  margin: -0.5rem 0 1rem;
  max-width: 720px;
}

.page-hint code {
  background: var(--bg-input);
  border-radius: 4px;
  padding: 1px 5px;
}

.uri-line {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.78rem;
  color: var(--text-muted);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 1rem;
  overflow-y: auto;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.5rem;
  max-width: 480px;
  width: 100%;
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  margin: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.modal-header h2 {
  font-size: 1rem;
  font-weight: 600;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--text);
  cursor: pointer;
}
</style>
